// spec 260509-system-monitor B-2 / design.md §4.1：阈值常量集中维护。
//
// 前端用这套阈值做染色 + 主因选择派生。后端只透传原始数据，不做阈值判断。
// 阈值未来可改为后端配置下发，但本期固定在前端常量里（Stage A 的 design.md
// §4.1 留下了升级路径）。

import type { TemperatureKind } from './system-types';

export type Severity = 'ok' | 'warn' | 'crit';

export const THRESHOLDS = {
  cpu: { warn: 70, crit: 90 },
  gpu_util: { warn: 80, crit: 95 },
  // 内存阈值故意调高（spec 260509 用户反馈）：Linux 下 ``mem_percent`` 包括
  // cache/buffer，常态下 70-85% 是健康水位（操作系统主动占满做磁盘缓存），传统
  // 75/90 在 Linux 桌面/服务器会长期假 warn。配合 ``available`` + ``swap`` 联合
  // 判定，把 mem_percent 的硬阈值放到 95/98 才报警。
  mem_percent: { warn: 95, crit: 98 },
  // patch 9：联合判定。DGX Spark 等统一内存架构机器 swap 常态 20-30%，
  // 传统阈值 10 会长期误报。调到 50 只在真正内存压力时才 warn。
  swap_percent_warn: 50,
  available_bytes_warn: 1 * 1024 ** 3, // 1 GB
  available_bytes_crit: 256 * 1024 ** 2, // 256 MB
  // 温度阈值统一到 warn=90 / crit=95（用户反馈：原 per-kind 阈值在常态下会
  // 过早抬警；当前一体机里关心的是"任一传感器烫到接近降频"，统一阈值更直观）。
  temp: {
    cpu_pkg: { warn: 90, crit: 95 },
    cpu_core: { warn: 90, crit: 95 },
    gpu_die: { warn: 90, crit: 95 },
    gpu_hotspot: { warn: 90, crit: 95 },
    nvme: { warn: 90, crit: 95 },
    unknown: { warn: 90, crit: 95 },
  } satisfies Record<TemperatureKind, { warn: number; crit: number }>,
  disk_percent: { warn: 85, crit: 95 },
} as const;

// design.md §4.1 hysteresis：OK 态下，② 段瓶颈切换需要新候选连续 ≥ 3 秒
// 主导才生效；warn / crit 立即切。
export const HYSTERESIS = {
  bottleneckSwitchSeconds: 3,
} as const;

/** 把数值按 ``warn / crit`` 阈值映射到三态。 */
export function classify(
  value: number,
  warn: number,
  crit: number,
): Severity {
  if (value >= crit) return 'crit';
  if (value >= warn) return 'warn';
  return 'ok';
}

/** 温度按传感器 kind 查阈值。 */
export function classifyTemp(kind: TemperatureKind, value: number): Severity {
  const t = THRESHOLDS.temp[kind] ?? THRESHOLDS.temp.unknown;
  return classify(value, t.warn, t.crit);
}
